import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Wand2, ScanSearch, ChevronDown, ChevronRight,
  AlertTriangle, ArrowUp, Minus, ArrowDown, Loader2,
  History, X, Filter, Clock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProjectContext } from '../context/ProjectContext';
import { ArtifactModal } from '../components/ArtifactModal';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const MAX_SELECTION = 30;

// ── Types ──────────────────────────────────────────────────────────────────────

type RiskLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

interface ITestNode {
  id: string;
  title: string;
  riskLevel: RiskLevel;
}

interface ITestGroup {
  suite: string;
  riskLevel: RiskLevel;
  rationale: string;
  tests: ITestNode[];
}

interface IDiscoveryResult {
  projectId: string;
  projectName: string;
  totalScanned: number;
  groups: ITestGroup[];
  summary: string;
  filtersApplied: { suites: string[] | null; tags: string[] | null } | null;
  planId?: string;
}

interface ISavedPlan {
  _id: string;
  projectId: string;
  projectName: string;
  testIds: string[];
  framework: string;
  designPattern: string;
  markdown: string;
  createdAt: string;
  status: 'DRAFT' | 'COMPLETED';
  discoveryResult?: IDiscoveryResult;
}

interface IFilterOptions {
  suites: string[];
  tags: string[];
}

// ── Risk level config ──────────────────────────────────────────────────────────

const RISK_CONFIG: Record<RiskLevel, { label: string; badgeClass: string; icon: typeof AlertTriangle; rowClass: string }> = {
  CRITICAL: {
    label: 'Critical',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: AlertTriangle,
    rowClass: 'border-l-2 border-red-400',
  },
  HIGH: {
    label: 'High',
    badgeClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    icon: ArrowUp,
    rowClass: 'border-l-2 border-orange-400',
  },
  MEDIUM: {
    label: 'Medium',
    badgeClass: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: Minus,
    rowClass: 'border-l-2 border-yellow-400',
  },
  LOW: {
    label: 'Low',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: ArrowDown,
    rowClass: 'border-l-2 border-blue-400',
  },
};

function RiskBadge({ level }: { level: RiskLevel }) {
  const cfg = RISK_CONFIG[level];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.badgeClass}`}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

// ── Display helpers ────────────────────────────────────────────────────────────

const FRAMEWORK_LABELS: Record<string, string> = {
  playwright: 'Playwright', cypress: 'Cypress', pytest: 'Pytest',
  vitest: 'Vitest', jest: 'Jest', webdriverio: 'WebdriverIO',
};
const PATTERN_LABELS: Record<string, string> = {
  'page-object': 'Page Object', screenplay: 'Screenplay', fixture: 'Fixture',
  fluent: 'Fluent', 'data-driven': 'Data-Driven',
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ── SuiteGroup ─────────────────────────────────────────────────────────────────

function SuiteGroup({
  group, selectedIds, onToggle, selectionFull,
}: {
  group: ITestGroup;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  selectionFull: boolean;
}) {
  const [open, setOpen] = useState(true);
  const selectedInGroup = group.tests.filter((t) => selectedIds.has(t.id)).length;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {open
            ? <ChevronDown size={14} className="flex-shrink-0 text-slate-400 dark:text-slate-500" />
            : <ChevronRight size={14} className="flex-shrink-0 text-slate-400 dark:text-slate-500" />
          }
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{group.suite}</span>
          <RiskBadge level={group.riskLevel} />
          <span className="text-xs text-slate-400 dark:text-slate-500">({group.tests.length})</span>
        </div>
        {selectedInGroup > 0 && (
          <span className="flex-shrink-0 text-xs font-medium text-violet-600 dark:text-violet-400">
            {selectedInGroup} selected
          </span>
        )}
      </button>

      {open && group.rationale && (
        <p className="px-4 py-1.5 text-xs text-slate-500 dark:text-slate-400 italic border-b border-slate-100 dark:border-slate-700/60">
          {group.rationale}
        </p>
      )}

      {open && (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
          {group.tests.map((test) => {
            const isChecked = selectedIds.has(test.id);
            const isDisabled = !isChecked && selectionFull;
            return (
              <li
                key={test.id}
                className={`${RISK_CONFIG[test.riskLevel].rowClass} ${
                  isDisabled ? 'opacity-40' : ''
                }`}
              >
                <label
                  className={`flex items-center gap-3 px-4 py-2.5 w-full ${
                    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => onToggle(test.id)}
                    className="flex-shrink-0 w-4 h-4 rounded accent-violet-600 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <span className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate">{test.title}</span>
                  <RiskBadge level={test.riskLevel} />
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── HistoryPanel ───────────────────────────────────────────────────────────────

function HistoryPanel({
  plans, loading, selectedPlanId, onSelect, onClose,
}: {
  plans: ISavedPlan[];
  loading: boolean;
  selectedPlanId: string | null;
  onSelect: (plan: ISavedPlan) => void;
  onClose: () => void;
}) {
  return (
    <div className="w-64 flex-shrink-0 flex flex-col border-l border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <History size={14} className="text-violet-500" />
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Saved Plans</span>
        </div>
        <button
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          aria-label="Close history"
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto py-1">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={20} className="animate-spin text-violet-400" />
          </div>
        )}

        {!loading && plans.length === 0 && (
          <div className="text-center py-10 px-4">
            <Clock size={22} className="mx-auto mb-2 text-slate-300 dark:text-slate-600" />
            <p className="text-xs text-slate-400 dark:text-slate-500">No saved plans yet.</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Generate a plan to see it here.</p>
          </div>
        )}

        {!loading && plans.map((plan) => {
          const isSelected = plan._id === selectedPlanId;
          return (
            <button
              key={plan._id}
              onClick={() => onSelect(plan)}
              className={`w-full text-left px-4 py-3 border-b border-slate-200 dark:border-slate-700/60 transition-colors ${
                isSelected
                  ? 'bg-violet-50 dark:bg-violet-900/20 border-l-2 border-l-violet-500'
                  : 'hover:bg-white dark:hover:bg-slate-800'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
                  {plan.projectName}
                </p>
                {plan.status === 'DRAFT' && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                    Draft
                  </span>
                )}
              </div>
              
              {plan.status !== 'DRAFT' && (
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400">
                    {FRAMEWORK_LABELS[plan.framework] ?? plan.framework}
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    {PATTERN_LABELS[plan.designPattern] ?? plan.designPattern}
                  </span>
                </div>
              )}
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                {plan.status !== 'DRAFT' ? `${plan.testIds.length} test${plan.testIds.length !== 1 ? 's' : ''} · ` : ''}{fmtDate(plan.createdAt)}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── MultiSelectDropdown ────────────────────────────────────────────────────────
//
// A custom checkbox-based dropdown that allows selecting multiple values.
// Replaces the native <select> to support multi-selection with a clean UI.

function MultiSelectDropdown({
  options,
  values,
  onChange,
  disabled,
  loading,
  allLabel,
}: {
  options: string[];
  values: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
  loading: boolean;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close when the user clicks outside the dropdown
  useEffect(() => {
    if (!open) return;
    function handleOutsideClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  function toggleOption(option: string) {
    if (values.includes(option)) {
      onChange(values.filter((v) => v !== option));
    } else {
      onChange([...values, option]);
    }
  }

  const triggerLabel =
    values.length === 0
      ? allLabel
      : values.length === 1
        ? values[0]
        : `${values.length} selected`;

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => !disabled && !loading && setOpen((v) => !v)}
        disabled={disabled || loading}
        className="w-full flex items-center justify-between pl-3 pr-2.5 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50 transition-colors"
      >
        <span className={`truncate ${values.length === 0 ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>
          {loading ? 'Loading…' : triggerLabel}
        </span>
        <ChevronDown
          size={13}
          className={`flex-shrink-0 ml-2 text-slate-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1 w-full min-w-[180px] max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg">
          {options.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-slate-400 dark:text-slate-500 italic">
              No options available
            </p>
          ) : (
            options.map((opt) => {
              const checked = values.includes(opt);
              return (
                <label
                  key={opt}
                  className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors select-none"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOption(opt)}
                    className="w-3.5 h-3.5 rounded accent-violet-600 flex-shrink-0 cursor-pointer"
                  />
                  <span className={`text-sm truncate ${checked ? 'text-violet-700 dark:text-violet-300 font-medium' : 'text-slate-700 dark:text-slate-200'}`}>
                    {opt}
                  </span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── FilterBar ──────────────────────────────────────────────────────────────────

function FilterBar({
  options,
  optionsLoading,
  filterSuites,
  filterTags,
  onSuitesChange,
  onTagsChange,
  onClear,
  disabled,
}: {
  options: IFilterOptions;
  optionsLoading: boolean;
  filterSuites: string[];
  filterTags: string[];
  onSuitesChange: (v: string[]) => void;
  onTagsChange: (v: string[]) => void;
  onClear: () => void;
  disabled: boolean;
}) {
  const hasFilters = filterSuites.length > 0 || filterTags.length > 0;

  return (
    <div className="mt-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row items-start sm:items-end gap-3">
      {/* Suite multi-select */}
      <div className="flex-1 min-w-0 w-full sm:w-auto">
        <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
          Suite
        </label>
        <MultiSelectDropdown
          options={options.suites}
          values={filterSuites}
          onChange={onSuitesChange}
          disabled={disabled}
          loading={optionsLoading}
          allLabel="All suites"
        />
      </div>

      {/* Tag multi-select */}
      <div className="flex-1 min-w-0 w-full sm:w-auto">
        <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
          Tag
        </label>
        <MultiSelectDropdown
          options={options.tags}
          values={filterTags}
          onChange={onTagsChange}
          disabled={disabled}
          loading={optionsLoading}
          allLabel="All tags"
        />
      </div>

      {optionsLoading && (
        <Loader2 size={14} className="animate-spin text-slate-400 mb-2 flex-shrink-0" />
      )}

      {/* Clear button */}
      <button
        onClick={onClear}
        disabled={!hasFilters || disabled}
        className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Clear filters
      </button>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function AutomationPlannerPage() {
  const { token } = useAuth();
  const { projects, activeProjectId, setActiveProjectId } = useProjectContext();

  // ── Scan state ─────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<'idle' | 'scanning' | 'results' | 'error'>('idle');
  const [result, setResult] = useState<IDiscoveryResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedProject, setSelectedProject] = useState(activeProjectId);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [filterSuites, setFilterSuites] = useState<string[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [filterOptions, setFilterOptions] = useState<IFilterOptions>({ suites: [], tags: [] });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);

  // ── History state ──────────────────────────────────────────────────────────
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<ISavedPlan[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryPlan, setSelectedHistoryPlan] = useState<ISavedPlan | null>(null);

  // ── Artifact modal state ───────────────────────────────────────────────────
  const [showArtifact, setShowArtifact] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const urlPlanId = searchParams.get('planId');

  useEffect(() => {
    if (urlPlanId && !result && phase === 'idle' && token) {
      setPhase('scanning');
      axios.get(`${API_URL}/api/ai/automation-planner/history`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        if (res.data.success) {
          const fetchedPlans: ISavedPlan[] = res.data.data;
          setHistory(fetchedPlans);
          const draft = fetchedPlans.find(p => p._id === urlPlanId && p.status === 'DRAFT');
          if (draft && draft.discoveryResult) {
            setSelectedProject(draft.projectId);
            setActiveProjectId(draft.projectId);
            setResult({ ...draft.discoveryResult, planId: draft._id });
            setPhase('results');
          } else {
            setPhase('idle');
          }
        }
      }).catch(() => {
        setPhase('idle');
      });
    }
  }, [urlPlanId, token]);

  const selectionCount = selectedIds.size;
  const selectionFull = selectionCount >= MAX_SELECTION;

  // ── Load filter options whenever a project is selected ─────────────────────

  useEffect(() => {
    if (!selectedProject || !token) return;

    setFilterOptions({ suites: [], tags: [] });
    setFilterOptionsLoading(true);

    axios
      .get(`${API_URL}/api/ai/automation-planner/filter-options?projectId=${encodeURIComponent(selectedProject)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.success) {
          setFilterOptions(res.data.data as IFilterOptions);
        }
      })
      .catch(() => { /* Non-fatal — dropdowns stay empty */ })
      .finally(() => setFilterOptionsLoading(false));
  }, [selectedProject, token]);

  // ── Load history ────────────────────────────────────────────────────────────

  const loadHistory = useCallback(async () => {
    if (!token) return;
    setHistoryLoading(true);
    try {
      const params = selectedProject ? `?projectId=${encodeURIComponent(selectedProject)}` : '';
      const res = await axios.get(
        `${API_URL}/api/ai/automation-planner/history${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) setHistory(res.data.data as ISavedPlan[]);
    } catch {
      // Non-fatal — silently ignore
    } finally {
      setHistoryLoading(false);
    }
  }, [token, selectedProject]);

  useEffect(() => {
    if (showHistory) loadHistory();
  }, [showHistory, loadHistory]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleToggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_SELECTION) next.add(id);
      return next;
    });
  }

  function handleSelectAll() {
    if (!result) return;
    const allIds = result.groups.flatMap(g => g.tests.map(t => t.id));
    
    // We consider "All Selected" if we've either selected every possible test,
    // OR if we've hit the MAX_SELECTION limit.
    const isAllSelected = selectedIds.size > 0 && selectedIds.size === Math.min(allIds.length, MAX_SELECTION);
    
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      const next = new Set<string>();
      for (const id of allIds) {
        if (next.size >= MAX_SELECTION) break;
        next.add(id);
      }
      setSelectedIds(next);
    }
  }

  async function handleScan() {
    if (!selectedProject) return;
    setPhase('scanning');
    setResult(null);
    setSelectedIds(new Set());
    setErrorMsg(null);

    const body: Record<string, unknown> = { projectId: selectedProject };
    if (filterSuites.length > 0) body.suites = filterSuites;
    if (filterTags.length > 0) body.tags = filterTags;

    try {
      const res = await axios.post(
        `${API_URL}/api/ai/automation-planner/discover`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.data.success) throw new Error(res.data.error ?? 'Unknown error from server.');
      const newResult = res.data.data as IDiscoveryResult;
      setResult(newResult);
      setPhase('results');
      if (newResult.planId) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('planId', newResult.planId!);
          return next;
        }, { replace: true });
      }
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data?.error ?? err.message)
        : (err instanceof Error ? err.message : 'Unexpected error.');
      setErrorMsg(msg);
      setPhase('error');
    }
  }

  function handleRetry() {
    setPhase('idle');
    setResult(null);
    setErrorMsg(null);
    setSelectedIds(new Set());
  }

  function handleProjectChange(projectId: string) {
    setSelectedProject(projectId);
    setActiveProjectId(projectId);
    setFilterSuites([]);
    setFilterTags([]);
    handleRetry();
  }

  function handleHistorySelect(plan: ISavedPlan) {
    if (plan.status === 'DRAFT' && plan.discoveryResult) {
      setResult({ ...plan.discoveryResult, planId: plan._id });
      setPhase('results');
      setSelectedProject(plan.projectId);
      setActiveProjectId(plan.projectId);
      setSelectedIds(new Set()); // Reset selected checkboxes
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('planId', plan._id);
        return next;
      }, { replace: true });
      setShowHistory(false);
    } else {
      setSelectedHistoryPlan(plan);
      setShowArtifact(true);
    }
  }

  function handleArtifactClose() {
    setShowArtifact(false);
    setSelectedHistoryPlan(null);
  }

  const hasActiveFilters = filterSuites.length > 0 || filterTags.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-700">

        {/* Top row */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Title */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex-shrink-0">
              <Wand2 size={18} className="text-violet-600 dark:text-violet-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-slate-900 dark:text-white leading-tight">
                Automation Planner
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                AI-powered risk analysis of your manual test coverage
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* History toggle */}
            <button
              onClick={() => setShowHistory((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                showHistory
                  ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 border-violet-300 dark:border-violet-700'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <History size={14} />
              History
              {history.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-violet-600 text-white leading-none">
                  {history.length}
                </span>
              )}
            </button>

            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters((v) => !v)}
              disabled={phase === 'scanning'}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
                showFilters || hasActiveFilters
                  ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 border-violet-300 dark:border-violet-700'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <Filter size={14} />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              )}
            </button>

            {/* Project selector */}
            {projects.length > 0 && (
              <div className="relative">
                <select
                  value={selectedProject}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  disabled={phase === 'scanning'}
                  className="appearance-none text-sm pl-3 pr-8 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
                >
                  <option value="">Select project…</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            )}

            {/* Scan / Re-scan */}
            <button
              onClick={phase === 'results' ? handleRetry : handleScan}
              disabled={!selectedProject || phase === 'scanning'}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {phase === 'scanning' ? (
                <><Loader2 size={14} className="animate-spin" /> Scanning…</>
              ) : phase === 'results' ? (
                <><ScanSearch size={14} /> Re-scan</>
              ) : (
                <><ScanSearch size={14} /> Scan Manual Tests</>
              )}
            </button>
          </div>
        </div>

        {/* Filter bar */}
        {showFilters && (
          <FilterBar
            options={filterOptions}
            optionsLoading={filterOptionsLoading}
            filterSuites={filterSuites}
            filterTags={filterTags}
            onSuitesChange={setFilterSuites}
            onTagsChange={setFilterTags}
            onClear={() => { setFilterSuites([]); setFilterTags([]); }}
            disabled={phase === 'scanning'}
          />
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden flex">

        {/* Main content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">

          {/* IDLE */}
          {phase === 'idle' && (
            <div className="flex flex-col items-center justify-center h-full gap-5 text-center">
              <div className="relative flex items-center justify-center w-20 h-20">
                <div className="absolute inset-0 rounded-2xl bg-violet-50 dark:bg-violet-900/20" />
                <ScanSearch size={34} className="relative text-violet-500 dark:text-violet-400" />
              </div>
              <div className="max-w-sm">
                <p className="text-base font-semibold text-slate-800 dark:text-white mb-1">
                  Ready to analyse your test coverage
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                  Select a project above and click <strong className="text-slate-700 dark:text-slate-200">Scan Manual Tests</strong>.
                  Use <strong className="text-slate-700 dark:text-slate-200">Filters</strong> to narrow
                  the scan to a specific suite or tag.
                </p>
              </div>
              {!selectedProject && projects.length > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                  ← Please select a project first
                </p>
              )}
            </div>
          )}

          {/* SCANNING */}
          {phase === 'scanning' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Loader2 size={36} className="animate-spin text-violet-500" />
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Analysing test coverage…
              </p>
              <p className="text-xs text-slate-400">
                Fetching test metadata and running AI risk stratification
              </p>
            </div>
          )}

          {/* ERROR */}
          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-900/20">
                <AlertTriangle size={26} className="text-red-500" />
              </div>
              <div className="text-center max-w-sm">
                <p className="text-sm font-semibold text-slate-800 dark:text-white mb-1">Scan failed</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{errorMsg}</p>
              </div>
              <button
                onClick={handleRetry}
                className="text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* RESULTS */}
          {phase === 'results' && result && (
            <div className="flex flex-col gap-5">

              {/* Filters-applied banner */}
              {result.filtersApplied && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/40 text-xs text-violet-700 dark:text-violet-300">
                  <Filter size={12} className="flex-shrink-0" />
                  <span className="flex flex-wrap items-center gap-x-1">
                    Scan narrowed by filters:
                    {result.filtersApplied.suites && result.filtersApplied.suites.length > 0 && (
                      <strong>
                        Suite{result.filtersApplied.suites.length > 1 ? 's' : ''}=
                        [{result.filtersApplied.suites.map((s) => `"${s}"`).join(', ')}]
                      </strong>
                    )}
                    {result.filtersApplied.tags && result.filtersApplied.tags.length > 0 && (
                      <strong>Tags=[{result.filtersApplied.tags.join(', ')}]</strong>
                    )}
                  </span>
                </div>
              )}

              {/* Summary banner */}
              {result.summary && (
                <div className="px-4 py-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/40">
                  <p className="text-sm text-violet-800 dark:text-violet-300 leading-relaxed">{result.summary}</p>
                </div>
              )}

              {/* Stats row */}
              <div className="flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
                <span>
                  <strong className="text-slate-700 dark:text-slate-200">{result.totalScanned}</strong>{' '}
                  test{result.totalScanned !== 1 ? 's' : ''} scanned
                </span>
                <span>
                  <strong className="text-slate-700 dark:text-slate-200">{result.groups.length}</strong>{' '}
                  suite{result.groups.length !== 1 ? 's' : ''}
                </span>

                {result.groups.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer select-none ml-auto border-l border-slate-200 dark:border-slate-700 pl-6">
                    <input
                      type="checkbox"
                      checked={selectionCount > 0 && selectionCount === Math.min(result.groups.reduce((acc, g) => acc + g.tests.length, 0), MAX_SELECTION)}
                      onChange={handleSelectAll}
                      className="rounded border-slate-300 dark:border-slate-600 text-violet-600 focus:ring-violet-500 cursor-pointer w-4 h-4 mt-0.5"
                    />
                    <span className="font-semibold text-slate-700 dark:text-slate-200">Select All</span>
                  </label>
                )}

                {result.totalScanned > 0 && (
                  <span className={`font-semibold ${selectionFull ? 'text-amber-600 dark:text-amber-400' : 'text-violet-600 dark:text-violet-400'} ${result.groups.length === 0 ? 'ml-auto' : ''}`}>
                    Selected: {selectionCount} / {MAX_SELECTION}
                  </span>
                )}
              </div>

              {/* Hard-cap warning */}
              {selectionFull && (
                <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-xs text-amber-700 dark:text-amber-400 font-medium">
                  Maximum of {MAX_SELECTION} tests selected. Deselect a test to choose a different one.
                </div>
              )}

              {/* Empty state */}
              {result.groups.length === 0 && (
                <div className="text-center py-16 text-slate-400 dark:text-slate-500 text-sm">
                  {result.summary}
                </div>
              )}

              {/* Suite groups */}
              {result.groups.map((group) => (
                <SuiteGroup
                  key={group.suite}
                  group={group}
                  selectedIds={selectedIds}
                  onToggle={handleToggle}
                  selectionFull={selectionFull}
                />
              ))}

              {/* Generate CTA — sticky bottom bar */}
              {selectionCount > 0 && (
                <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <strong className="text-slate-800 dark:text-white">{selectionCount}</strong>{' '}
                    test{selectionCount !== 1 ? 's' : ''} selected for plan generation
                  </p>
                  <button
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                    onClick={() => { setSelectedHistoryPlan(null); setShowArtifact(true); }}
                  >
                    <Wand2 size={14} />
                    Generate Plan
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* History side panel */}
        {showHistory && (
          <HistoryPanel
            plans={history}
            loading={historyLoading}
            selectedPlanId={selectedHistoryPlan?._id ?? null}
            onSelect={handleHistorySelect}
            onClose={() => setShowHistory(false)}
          />
        )}
      </div>

      {/* Normal artifact modal — user selected tests, calls /generate */}
      {showArtifact && result && !selectedHistoryPlan && (
        <ArtifactModal
          projectId={result.projectId}
          projectName={result.projectName}
          selectedIds={[...selectedIds]}
          onClose={handleArtifactClose}
          onGenerated={loadHistory}
          planId={result.planId}
        />
      )}

      {/* History artifact modal — pre-populated markdown, no LLM call */}
      {showArtifact && selectedHistoryPlan && (
        <ArtifactModal
          projectId={selectedHistoryPlan.projectId}
          projectName={selectedHistoryPlan.projectName}
          selectedIds={selectedHistoryPlan.testIds}
          initialMarkdown={selectedHistoryPlan.markdown}
          onClose={handleArtifactClose}
        />
      )}
    </div>
  );
}
